import { Settings as SettingsIcon } from 'lucide-react'
import type { ComponentType, KeyboardEvent, ReactNode } from 'react'
import { useAuth } from '@auth/AuthContext.tsx'
import { Emoji } from '@components/Emoji.tsx'
import { InviteCode } from '@components/InviteCode.tsx'
import { APPS, type AppSlug } from '@config'
import { useFavicon } from '@hooks/useFavicon.ts'
import { Link, useRouter } from '@router'
import { MealsWidget } from '@apps/meals/MealsWidget.tsx'
import { TodoWidget } from '@apps/todo/TodoWidget.tsx'
import styles from './Landing.module.css'

type Widget = {
	component: ComponentType
	colSpan: number
	rowSpan: number
}

// Landing widget registry. Apps in this map render their widget component
// in place of the default emoji+name tile. Edit colSpan/rowSpan to resize
// or swap a component to change what's surfaced.
const WIDGETS: Partial<Record<AppSlug, Widget>> = {
	todo: { component: TodoWidget, colSpan: 2, rowSpan: 1 },
	meals: { component: MealsWidget, colSpan: 2, rowSpan: 1 },
}

// Render order, top-to-bottom / left-to-right. Slugs omitted here are
// hidden from the landing entirely. Keep wide widgets at the top so the
// grid fills without holes.
const ORDER: AppSlug[] = ['meals', 'todo']

type CellProps = {
	slug: AppSlug
	tint: string
	colSpan: number
	rowSpan: number
	children: ReactNode
}

const Cell = ({ slug, tint, colSpan, rowSpan, children }: CellProps) => {
	const { navigate } = useRouter()
	const go = () => navigate(`/${slug}`)
	const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		// Only respond when the cell itself is focused — inner links handle
		// their own keyboard activation.
		if (e.target !== e.currentTarget) return
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			go()
		}
	}
	return (
		<div
			role="link"
			tabIndex={0}
			className={`${styles.cell} ${styles[`tint-${tint}`]}`}
			style={{
				gridColumn: `span ${colSpan}`,
				gridRow: `span ${rowSpan}`,
			}}
			onClick={go}
			onKeyDown={onKeyDown}
		>
			{children}
		</div>
	)
}

export const Landing = () => {
	useFavicon('hewey')
	const { state } = useAuth()
	const user = state.status === 'authed' ? state.user : null
	const household = state.status === 'authed' ? state.household : null

	const appBySlug = new Map(APPS.map((a) => [a.slug, a]))
	const ordered = ORDER.flatMap((slug) => {
		const app = appBySlug.get(slug)
		return app ? [app] : []
	})

	return (
		<main className={styles.screen}>
			<header className={styles.header}>
				<div>
					<h1 className={styles.title}>
						Hi {user?.displayName ?? 'there'}{' '}
						<Emoji char="👋" label="waving hand" />
					</h1>
					<p className={styles.subtitle}>What are we up to today?</p>
				</div>
				<Link
					href="/settings"
					className={styles.settings}
					aria-label="Settings"
				>
					<SettingsIcon size={20} aria-hidden />
				</Link>
			</header>

			{household ? (
				<div className={styles.invite}>
					<InviteCode
						householdName={household.name}
						code={household.joinCode}
					/>
				</div>
			) : null}

			<div className={styles.grid}>
				{ordered.map((app) => {
					const widget = WIDGETS[app.slug]
					if (widget) {
						const WidgetComponent = widget.component
						return (
							<Cell
								key={app.slug}
								slug={app.slug}
								tint={app.tint}
								colSpan={widget.colSpan}
								rowSpan={widget.rowSpan}
							>
								<WidgetComponent />
							</Cell>
						)
					}
					return (
						<Cell
							key={app.slug}
							slug={app.slug}
							tint={app.tint}
							colSpan={1}
							rowSpan={1}
						>
							<div className={styles.defaultTile}>
								<span className={styles.emoji}>
									<Emoji char={app.emoji} label={app.name} />
								</span>
								<span className={styles.name}>{app.name}</span>
							</div>
						</Cell>
					)
				})}
			</div>
		</main>
	)
}
